import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Play,
    Pause,
    Volume2,
    VolumeX,
    Maximize,
    Minimize,
    SkipForward,
    SkipBack,
    X,
    AlertCircle,
    CheckCircle2,
    Clock,
    User,
    PlayCircle,
    ChevronDown,
    ChevronRight,
    Loader2,
    FileText,
    ArrowLeft,
    BookOpen,
    Shield,
    Award,
    Target,
    Video as VideoIcon,
    Layers,
    GraduationCap
} from 'lucide-react';
import { getMyCourseBySlug, updateVideoProgress } from '../api/api';

const CoursePlayer = ({ slug: propSlug }) => {
    const { slug: paramSlug } = useParams();
    const slug = propSlug || paramSlug;
    const navigate = useNavigate();

    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(1);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [controlsTimeout, setControlsTimeout] = useState(null);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showError, setShowError] = useState(false);
    const [courseData, setCourseData] = useState(null);
    const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [expandedModules, setExpandedModules] = useState({});
    const [isUpdatingProgress, setIsUpdatingProgress] = useState(false);

    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const progressUpdateTimeout = useRef(null);

    // Go back to courses
    const handleBackToCourses = () => {
        navigate('/dashboard', { state: { activeComponent: 'mycourses' } });
    };

    // Security: Prevent right-click, screen recording, and keyboard shortcuts
    useEffect(() => {
        const handleContextMenu = (e) => {
            e.preventDefault();
            return false;
        };

        const handleKeyDown = (e) => {
            if (
                (e.ctrlKey && (e.key === 's' || e.key === 'S' || e.key === 'u' || e.key === 'U')) ||
                (e.ctrlKey && e.shiftKey && (e.key === 'i' || e.key === 'I')) ||
                e.key === 'F12' ||
                (e.ctrlKey && e.key === 'p') ||
                (e.ctrlKey && e.key === 'P') ||
                (e.key === 'PrintScreen')
            ) {
                e.preventDefault();
                return false;
            }
        };

        const handleKeyUp = (e) => {
            if (e.key === 'PrintScreen') {
                e.preventDefault();
                return false;
            }
        };

        const handleCopy = (e) => {
            if (e.target.closest('video') || e.target.closest('.video-container')) {
                e.preventDefault();
                return false;
            }
        };

        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keyup', handleKeyUp);
        document.addEventListener('copy', handleCopy);

        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('keyup', handleKeyUp);
            document.removeEventListener('copy', handleCopy);
        };
    }, []);

    // Fetch course data by slug
    useEffect(() => {
        const fetchCourseData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                if (!slug) {
                    setError("No course slug provided");
                    setIsLoading(false);
                    return;
                }

                const response = await getMyCourseBySlug(slug);

                if (response && response.success === true && response.course) {
                    const course = response.course;
                    setCourseData(course);

                    const initialExpanded = {};
                    if (course.modules && course.modules.length > 0) {
                        initialExpanded[course.modules[0].id] = true;
                    }
                    setExpandedModules(initialExpanded);

                    if (course.modules && course.modules.length > 0) {
                        const firstModule = course.modules[0];
                        if (firstModule.videos && firstModule.videos.length > 0) {
                            setSelectedVideo(firstModule.videos[0]);
                            setCurrentModuleIndex(0);
                            setCurrentVideoIndex(0);
                        }
                    }

                    setError(null);
                } else {
                    setError(response?.message || "Failed to fetch course data");
                }
            } catch (err) {
                console.error("Error fetching course:", err);
                setError(err.message || "Failed to fetch course data");
            } finally {
                setIsLoading(false);
            }
        };

        fetchCourseData();
    }, [slug]);

    // Handle video selection
    const handleVideoSelect = (moduleIndex, videoIndex, video) => {
        if (selectedVideo && progress > 0) {
            saveProgress(selectedVideo.id, progress, currentTime);
        }

        setSelectedVideo(video);
        setCurrentModuleIndex(moduleIndex);
        setCurrentVideoIndex(videoIndex);
        setProgress(0);
        setCurrentTime(0);
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
        }
    };

    // Toggle module expansion
    const toggleModule = (moduleId) => {
        setExpandedModules(prev => ({
            ...prev,
            [moduleId]: !prev[moduleId]
        }));
    };

    // Save progress to API
    const saveProgress = async (videoId, progressPercentage, watchTime) => {
        if (isUpdatingProgress) return;

        try {
            setIsUpdatingProgress(true);

            const progressData = {
                progress_percentage: Math.round(progressPercentage),
                watch_time_seconds: Math.round(watchTime),
                is_completed: progressPercentage >= 95
            };

            const response = await updateVideoProgress(videoId, progressData);

            if (response && response.success) {
                setSelectedVideo(prev => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        progress: {
                            ...prev.progress,
                            progress_percentage: Math.round(progressPercentage),
                            watch_time_seconds: Math.round(watchTime),
                            is_completed: progressPercentage >= 95,
                            last_watched_at: new Date().toISOString()
                        }
                    };
                });
            }
        } catch (error) {
            console.error("Failed to save progress:", error);
        } finally {
            setIsUpdatingProgress(false);
        }
    };

    const handleStartAssessment = (assessment) => {
        navigate('/dashboard', {
            state: {
                activeComponent: 'assessment',
                assessmentId: assessment.id,
                assessmentTitle: assessment.title,
                moduleId: assessment.module_id,
                moduleTitle: assessment.module_title,
                courseId: courseData.id,
                courseTitle: courseData.title,
                passingScore: assessment.passing_score,
                totalQuestions: assessment.total_questions,
                timeLimit: assessment.time_limit_minutes,
                assessmentUrl: assessment.start_url,
                courseSlug: courseData.slug
            }
        });
    };

    useEffect(() => {
        if (videoRef.current && selectedVideo?.file_url) {
            const video = videoRef.current;
            video.load();

            if (selectedVideo.progress?.resume_position) {
                video.currentTime = selectedVideo.progress.resume_position;
                setCurrentTime(selectedVideo.progress.resume_position);
                setProgress((selectedVideo.progress.resume_position / video.duration) * 100);
            }

            const handleLoadedMetadata = () => {
                setDuration(video.duration);
                if (selectedVideo.progress?.resume_position) {
                    video.currentTime = selectedVideo.progress.resume_position;
                }
            };

            const handleError = () => {
                setError('Failed to load video. Please try again.');
                setShowError(true);
            };

            const handleEnded = () => {
                setIsPlaying(false);
                if (progress > 90) {
                    saveProgress(selectedVideo.id, 100, video.duration);
                }
            };

            video.addEventListener('loadedmetadata', handleLoadedMetadata);
            video.addEventListener('error', handleError);
            video.addEventListener('ended', handleEnded);

            return () => {
                video.removeEventListener('loadedmetadata', handleLoadedMetadata);
                video.removeEventListener('error', handleError);
                video.removeEventListener('ended', handleEnded);
            };
        }
    }, [selectedVideo]);

    useEffect(() => {
        if (isPlaying && selectedVideo && progress > 0) {
            if (progressUpdateTimeout.current) {
                clearTimeout(progressUpdateTimeout.current);
            }
            progressUpdateTimeout.current = setTimeout(() => {
                const currentWatchTime = videoRef.current?.currentTime || 0;
                saveProgress(selectedVideo.id, progress, currentWatchTime);
            }, 5000);
        }

        return () => {
            if (progressUpdateTimeout.current) {
                clearTimeout(progressUpdateTimeout.current);
            }
        };
    }, [isPlaying, progress, selectedVideo]);

    useEffect(() => {
        return () => {
            if (selectedVideo && progress > 0) {
                const currentWatchTime = videoRef.current?.currentTime || 0;
                saveProgress(selectedVideo.id, progress, currentWatchTime);
            }
        };
    }, []);

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play().catch(() => { });
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleProgress = () => {
        if (videoRef.current) {
            const currentProgress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
            setProgress(currentProgress);
            setCurrentTime(videoRef.current.currentTime);
        }
    };

    const handleSeek = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const width = rect.width;
        const percentage = x / width;
        if (videoRef.current) {
            videoRef.current.currentTime = percentage * videoRef.current.duration;
            setProgress(percentage * 100);
            setCurrentTime(videoRef.current.currentTime);
        }
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    const handleVolumeChange = (e) => {
        const value = parseFloat(e.target.value);
        setVolume(value);
        if (videoRef.current) {
            videoRef.current.volume = value;
            videoRef.current.muted = value === 0;
            setIsMuted(value === 0);
        }
    };

    const toggleFullscreen = () => {
        if (playerRef.current) {
            if (!document.fullscreenElement) {
                playerRef.current.requestFullscreen().catch(() => { });
                setIsFullscreen(true);
            } else {
                document.exitFullscreen().catch(() => { });
                setIsFullscreen(false);
            }
        }
    };

    const handleMouseMove = () => {
        setShowControls(true);
        clearTimeout(controlsTimeout);
        const timeout = setTimeout(() => {
            if (isPlaying) {
                setShowControls(false);
            }
        }, 3000);
        setControlsTimeout(timeout);
    };

    const handleMouseLeave = () => {
        if (isPlaying) {
            setShowControls(false);
        }
    };

    const formatTime = (time) => {
        if (!time || isNaN(time)) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const changePlaybackSpeed = (speed) => {
        setPlaybackSpeed(speed);
        if (videoRef.current) {
            videoRef.current.playbackRate = speed;
        }
    };

    const handleSkipForward = () => {
        if (videoRef.current) {
            const newTime = Math.min(videoRef.current.currentTime + 10, videoRef.current.duration);
            videoRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    const handleSkipBackward = () => {
        if (videoRef.current) {
            const newTime = Math.max(videoRef.current.currentTime - 10, 0);
            videoRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    const getVideoStatus = (video) => {
        if (video.progress) {
            if (video.progress.is_completed) return 'completed';
            if (video.progress.progress_percentage > 0) return 'in-progress';
        }
        return 'not-started';
    };

    const getProgressColor = () => {
        if (progress < 30) return 'bg-red-500';
        if (progress < 70) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    // Clean HTML content - remove all HTML tags and return plain text with proper formatting
    const cleanHTMLContent = (html) => {
        if (!html) return '';
        // Replace <br> with newlines
        let text = html.replace(/<br\s*\/?>/gi, '\n');
        // Replace </p> with newline to separate paragraphs
        text = text.replace(/<\/p>/gi, '\n');
        // Remove all other HTML tags
        text = text.replace(/<[^>]*>/g, '');
        // Decode HTML entities
        text = text.replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .trim();
        return text;
    };

    // Get plain text lines from HTML content
    const getContentLines = (html) => {
        if (!html) return [];
        const cleanText = cleanHTMLContent(html);
        // Split by newlines and filter out empty lines
        return cleanText.split('\n')
            .map(line => line.trim())
            .filter(line => line !== '');
    };

    // Render clean content with bullet points
 const renderCleanContent = (htmlContent) => {
    if (!htmlContent) return null;
    
    // If content contains HTML tags, render as HTML
    if (/<[a-z][\s\S]*>/i.test(htmlContent)) {
        return (
            <div 
                className="text-xs text-gray-500 mt-1 description-content"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
        );
    }
    
    // For plain text, split by lines or bullet points
    let lines = [];
    if (htmlContent.includes('\n')) {
        lines = htmlContent.split('\n').filter(line => line.trim());
    } else if (htmlContent.includes('•') || htmlContent.includes('*') || htmlContent.includes('-')) {
        lines = htmlContent.split(/[•\-*]\s*/).filter(line => line.trim());
    } else if (htmlContent.includes(',')) {
        lines = htmlContent.split(',').filter(line => line.trim());
    } else {
        lines = [htmlContent.trim()];
    }
    
    if (lines.length === 0) return null;
    
    return (
        <div className="space-y-1 mt-1">
            {lines.map((line, index) => {
                // Remove leading bullet points or numbers
                const cleanLine = line.replace(/^[\s]*[*•\-]\s*/, '').replace(/^\d+\.\s*/, '');
                return (
                    <p key={index} className="flex items-start gap-2 text-xs text-gray-500">
                        <span className="text-green-500 mt-0.5 flex-shrink-0">•</span>
                        <span>{cleanLine || line}</span>
                    </p>
                );
            })}
        </div>
    );
};

    // For backward compatibility - keep old function but use renderCleanContent instead
    const renderTextWithNewlines = (text) => {
        if (!text) return null;
        const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
        if (lines.length === 0) return null;
        return (
            <div className="space-y-1">
                {lines.map((line, index) => (
                    <p key={index} className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">•</span>
                        <span className="flex-1">{line.trim()}</span>
                    </p>
                ))}
            </div>
        );
    };

    const renderTitleWithNewlines = (title) => {
        if (!title) return null;
        const lines = title.split(/\r?\n/).filter(line => line.trim() !== '');
        if (lines.length === 0) return title;
        return (
            <div>
                {lines.map((line, index) => (
                    <div key={index} className={index > 0 ? 'mt-1' : ''}>
                        {line.trim()}
                    </div>
                ))}
            </div>
        );
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            switch (e.key) {
                case ' ':
                case 'k':
                    e.preventDefault();
                    togglePlay();
                    break;
                case 'f':
                    toggleFullscreen();
                    break;
                case 'm':
                    toggleMute();
                    break;
                case 'ArrowRight':
                    handleSkipForward();
                    break;
                case 'ArrowLeft':
                    handleSkipBackward();
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isPlaying]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#F6F7F9] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
                    <p className="mt-4 text-gray-500">Loading course...</p>
                </div>
            </div>
        );
    }

    if (error || !courseData) {
        return (
            <div className="min-h-screen bg-[#F6F7F9] flex items-center justify-center">
                <div className="text-center bg-white p-8 rounded-2xl shadow-sm max-w-md">
                    <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700">Failed to load course</h3>
                    <p className="text-gray-500 mt-2">{error || "Course not found"}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    const progressStats = courseData.progress_stats || {};
    const totalModules = courseData.modules?.length || 0;
    const totalVideos = progressStats.total_videos || 0;
    const completedVideos = progressStats.completed_videos || 0;
    const courseProgressPercentage = progressStats.progress_percentage || 0;

    const currentModule = courseData.modules?.[currentModuleIndex] || {};
    const currentAssessment = currentModule.assessments?.[0] || null;

    return (
        <div 
            className="min-h-screen bg-[#F6F7F9]"
            onContextMenu={(e) => {
                e.preventDefault();
                return false;
            }}
        >
            {isUpdatingProgress && (
                <div className="fixed bottom-4 right-4 z-50 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm">
                    <Loader2 size={16} className="animate-spin" />
                    <span>Saving progress...</span>
                </div>
            )}

            {showError && (
                <div className="fixed top-4 right-4 z-50 bg-red-600 text-white px-6 py-4 rounded-lg shadow-xl flex items-center gap-3 max-w-md">
                    <AlertCircle size={20} />
                    <span>{error}</span>
                    <button onClick={() => setShowError(false)} className="ml-4 hover:opacity-70">
                        <X size={18} />
                    </button>
                </div>
            )}

            {/* Header with Back Button */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-20">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleBackToCourses}
                            className="flex items-center gap-2 text-[#57b26a] hover:text-[#49aa5d] transition-colors"
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <div className="h-6 w-px bg-gray-300"></div>
                        <div className="flex-1 min-w-0">
                            <h1 className="text-xl font-bold text-gray-900 truncate">{courseData.title}</h1>
                            <p className="text-sm text-gray-500">{courseData.instructor?.name || "Instructor"}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Grid */}
            <div className="grid grid-cols-12 gap-0">

                {/* LEFT - Course Curriculum */}
                <div className="col-span-3 bg-white border-r border-gray-200 min-h-screen overflow-y-auto">
                    <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                        <div className="flex items-center gap-2 mb-2">
                            <Layers size={20} className="text-green-500" />
                            <h2 className="font-bold text-lg">Course Content</h2>
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                            <span>{totalModules} Modules</span>
                            <span>•</span>
                            <span>{totalVideos} Videos</span>
                        </div>
                        <div className="mt-3">
                            <div className="flex justify-between text-sm">
                                <span>Progress</span>
                                <span className="font-semibold text-green-600">{courseProgressPercentage}%</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full mt-1">
                                <div className="h-2 rounded-full bg-green-500 transition-all" style={{ width: `${courseProgressPercentage}%` }} />
                            </div>
                        </div>
                    </div>

                    <div className="pb-4">
                        {courseData.modules && courseData.modules.map((module, moduleIndex) => {
                            const isExpanded = expandedModules[module.id];
                            const moduleVideos = module.videos || [];
                            const moduleProgress = module.stats?.progress_percentage || 0;
                            const hasAssessment = module.assessments && module.assessments.length > 0;

                            return (
                                <div key={module.id} className="border-b border-gray-100">
                                    <button
                                        onClick={() => toggleModule(module.id)}
                                        className="w-full text-left p-4 hover:bg-gray-50 transition flex items-start gap-3"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-500">Module {moduleIndex + 1}</span>
                                                {module.stats?.is_completed && (
                                                    <CheckCircle2 size={14} className="text-green-500 flex-shrink-0" />
                                                )}
                                                {hasAssessment && (
                                                    <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                                        <FileText size={10} />
                                                        Assessment
                                                    </span>
                                                )}
                                            </div>
                                            <div className="font-medium text-sm mt-0.5">
                                                {renderCleanContent(module.title)}
                                            </div>
                                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                                                <span>{moduleVideos.length} videos</span>
                                                {module.stats && (
                                                    <>
                                                        <span>•</span>
                                                        <span>{moduleProgress}% complete</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <span className="text-gray-400 text-sm flex-shrink-0 mt-1">
                                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                        </span>
                                    </button>

                                    {isExpanded && (
                                        <div className="bg-gray-50">
                                            {moduleVideos.map((video, videoIndex) => {
                                                const status = getVideoStatus(video);
                                                const isSelected = selectedVideo?.id === video.id;

                                                return (
                                                    <button
                                                        key={video.id}
                                                        onClick={() => handleVideoSelect(moduleIndex, videoIndex, video)}
                                                        className={`w-full text-left px-6 py-3 flex items-start gap-3 hover:bg-gray-100 transition ${isSelected ? 'bg-green-50 border-l-4 border-green-500' : ''
                                                            }`}
                                                    >
                                                        {status === 'completed' && (
                                                            <CheckCircle2 size={16} className="text-green-500 flex-shrink-0 mt-1" />
                                                        )}
                                                        {status === 'in-progress' && (
                                                            <Clock size={16} className="text-yellow-500 flex-shrink-0 mt-1" />
                                                        )}
                                                        {status === 'not-started' && (
                                                            <PlayCircle size={16} className="text-gray-400 flex-shrink-0 mt-1" />
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <div className={`text-sm ${isSelected ? 'font-medium text-green-700' : 'text-gray-700'}`}>
                                                                {renderCleanContent(video.title)}
                                                            </div>
                                                            {video.description && (
                                                                <div className="text-xs text-gray-400 mt-1 line-clamp-2">
                                                                    {renderCleanContent(video.description)}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {video.progress?.progress_percentage > 0 && video.progress?.progress_percentage < 100 && (
                                                            <span className="text-xs text-gray-400 flex-shrink-0">{video.progress.progress_percentage}%</span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* CENTER - Video Player */}
                <div className="col-span-6 p-6 space-y-4 bg-[#F6F7F9]">
                    {/* Video Player */}
                    <div className="bg-black rounded-2xl overflow-hidden shadow-lg">
                        <div
                            ref={playerRef}
                            className="relative aspect-video flex items-center justify-center video-container"
                            onMouseMove={handleMouseMove}
                            onMouseLeave={handleMouseLeave}
                            onContextMenu={(e) => {
                                e.preventDefault();
                                return false;
                            }}
                        >
                            {selectedVideo?.file_url ? (
                                <video
                                    ref={videoRef}
                                    className="w-full h-full object-contain"
                                    onTimeUpdate={handleProgress}
                                    onClick={togglePlay}
                                    src={selectedVideo.file_url}
                                    poster={selectedVideo.thumbnail_url || courseData.image_url}
                                    playsInline
                                    controlsList="nodownload noremoteplayback noplaybackrate"
                                    disablePictureInPicture
                                    onContextMenu={(e) => {
                                        e.preventDefault();
                                        return false;
                                    }}
                                    onCopy={(e) => {
                                        e.preventDefault();
                                        return false;
                                    }}
                                />
                            ) : (
                                <div className="text-white text-center p-8">
                                    <PlayCircle size={64} className="mx-auto mb-4 opacity-50" />
                                    <p className="text-gray-400">Select a video to start learning</p>
                                </div>
                            )}

                            {/* Watermark overlay */}
                            {selectedVideo && (
                                <div className="absolute top-4 right-4 pointer-events-none opacity-20">
                                    <div className="bg-black/50 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
                                        <Shield size={12} />
                                        {courseData.title?.substring(0, 20) || 'Protected Content'}
                                    </div>
                                </div>
                            )}

                            {/* Central Play Button */}
                            {selectedVideo && !isPlaying && !isLoading && (
                                <button
                                    onClick={togglePlay}
                                    className="absolute inset-0 flex items-center justify-center group"
                                >
                                    <div className="w-20 h-20 rounded-full bg-green-600/90 hover:bg-green-600 flex items-center justify-center transition transform group-hover:scale-105 shadow-xl">
                                        <Play size={40} className="ml-1 text-white" />
                                    </div>
                                </button>
                            )}

                            {/* Video Controls */}
                            {selectedVideo && (
                                <div
                                    className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'
                                        }`}
                                >
                                    {/* Progress Bar */}
                                    <div className="mb-3">
                                        <div
                                            className="relative h-1 bg-gray-600 rounded-full cursor-pointer group"
                                            onClick={handleSeek}
                                        >
                                            <div
                                                className={`h-full ${getProgressColor()} rounded-full transition-all`}
                                                style={{ width: `${progress}%` }}
                                            />
                                            <div
                                                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition shadow-lg"
                                                style={{ left: `${progress}%` }}
                                            />
                                        </div>
                                    </div>

                                    {/* Controls Row */}
                                    <div className="flex items-center justify-between text-white">
                                        <div className="flex items-center gap-1">
                                            <button onClick={togglePlay} className="p-2 hover:bg-white/10 rounded-lg transition">
                                                {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                                            </button>
                                            <button onClick={handleSkipBackward} className="p-2 hover:bg-white/10 rounded-lg transition">
                                                <SkipBack size={16} />
                                            </button>
                                            <button onClick={handleSkipForward} className="p-2 hover:bg-white/10 rounded-lg transition">
                                                <SkipForward size={16} />
                                            </button>
                                            <div className="flex items-center gap-2 ml-2">
                                                <button onClick={toggleMute} className="p-2 hover:bg-white/10 rounded-lg transition">
                                                    {isMuted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
                                                </button>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="1"
                                                    step="0.01"
                                                    value={volume}
                                                    onChange={handleVolumeChange}
                                                    className="w-20 h-1 bg-gray-600 rounded-full appearance-none cursor-pointer"
                                                />
                                            </div>
                                            <span className="text-sm ml-2">
                                                {formatTime(currentTime)} / {formatTime(duration)}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-1">
                                            <button 
                                                onClick={() => {
                                                    const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
                                                    const currentIndex = speeds.indexOf(playbackSpeed);
                                                    const nextIndex = (currentIndex + 1) % speeds.length;
                                                    changePlaybackSpeed(speeds[nextIndex]);
                                                }}
                                                className="p-2 hover:bg-white/10 rounded-lg transition text-xs font-medium"
                                            >
                                                {playbackSpeed}x
                                            </button>
                                            <button onClick={toggleFullscreen} className="p-2 hover:bg-white/10 rounded-lg transition">
                                                {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Video Info with Title and Description */}
                    {selectedVideo && (
                        <div className="bg-white rounded-xl shadow-sm p-5">
                            <div className="font-semibold text-lg">
                                {renderCleanContent(selectedVideo.title)}
                            </div>

                            <p className="text-gray-500 text-sm mt-1">
                                Module: {selectedVideo.module_title || `Module ${currentModuleIndex + 1}`}
                            </p>

                            {selectedVideo.description && (
                                <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                        <BookOpen size={16} className="text-green-500" />
                                        <h4 className="text-sm font-medium text-gray-700">Description</h4>
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {renderCleanContent(selectedVideo.description)}
                                    </div>
                                </div>
                            )}

                            {selectedVideo.progress && (
                                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                                    <span className={`px-3 py-1 rounded-full flex items-center gap-1 ${selectedVideo.progress.is_completed
                                        ? 'bg-green-100 text-green-700'
                                        : selectedVideo.progress.progress_percentage > 0
                                            ? 'bg-yellow-100 text-yellow-700'
                                            : 'bg-gray-100 text-gray-600'
                                        }`}>
                                        {selectedVideo.progress.is_completed
                                            ? <><CheckCircle2 size={14} /> Completed</>
                                            : selectedVideo.progress.progress_percentage > 0
                                                ? `${selectedVideo.progress.progress_percentage}% watched`
                                                : 'Not started'}
                                    </span>
                                    {selectedVideo.progress.last_watched_at && (
                                        <span className="text-gray-400">Last watched: {new Date(selectedVideo.progress.last_watched_at).toLocaleDateString()}</span>
                                    )}
                                    {selectedVideo.progress.watch_time_seconds > 0 && (
                                        <span className="text-gray-400">
                                            Watch time: {Math.floor(selectedVideo.progress.watch_time_seconds / 60)}m {Math.floor(selectedVideo.progress.watch_time_seconds % 60)}s
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Module Assessment Prompt */}
                    {currentAssessment && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                                    <Award size={20} className="text-yellow-600" />
                                </div>
                                <div>
                                    <p className="font-medium text-gray-800">{currentAssessment.title}</p>
                                    <p className="text-sm text-gray-500">
                                        {currentAssessment.user_progress?.is_passed
                                            ? '✅ You passed this assessment!'
                                            : currentAssessment.user_progress?.status === 'in_progress'
                                                ? '⏳ Continue your assessment'
                                                : '📝 Ready to take the assessment'}
                                    </p>
                                </div>
                            </div>
                            {!currentAssessment.user_progress?.is_passed && (
                                <button
                                    onClick={() => handleStartAssessment(currentAssessment)}
                                    className="px-5 py-2.5 rounded-lg text-sm font-medium transition bg-green-600 text-white hover:bg-green-700 whitespace-nowrap"
                                >
                                    {currentAssessment.user_progress?.status === 'in_progress'
                                        ? 'Continue Assessment'
                                        : 'Start Assessment →'}
                                </button>
                            )}
                        </div>
                    )}
                    
                    {/* Module Description */}
                    {courseData.modules && courseData.modules[currentModuleIndex]?.description && (
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <BookOpen size={16} className="text-blue-600" />
                                <h4 className="font-medium text-blue-800 text-sm">Module Overview</h4>
                            </div>
                            <div className="text-sm text-blue-700">
                                {renderCleanContent(courseData.modules[currentModuleIndex].description)}
                            </div>
                        </div>
                    )}
                </div>

                {/* RIGHT - Course Info & Progress */}
                <div className="col-span-3 bg-white border-l border-gray-200 p-6 space-y-6">
                    {/* Course Stats */}
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <Target size={20} className="text-green-500" />
                            <h3 className="font-bold text-lg">Course Progress</h3>
                        </div>
                        <div className="mt-4">
                            <div className="text-4xl font-bold text-green-600">{courseProgressPercentage}%</div>
                            <div className="h-2 rounded-full bg-gray-200 mt-3">
                                <div className="h-2 rounded-full bg-green-500 transition-all" style={{ width: `${courseProgressPercentage}%` }} />
                            </div>
                            <div className="flex justify-between mt-2 text-sm text-gray-500">
                                <span>{courseProgressPercentage}% Complete</span>
                                <span>{100 - courseProgressPercentage}% Remaining</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 mt-6 text-center border-t pt-4">
                            <div>
                                <p className="text-2xl font-bold text-gray-800">{completedVideos}</p>
                                <p className="text-xs text-gray-500">Completed</p>
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-800">{totalVideos - completedVideos}</p>
                                <p className="text-xs text-gray-500">Remaining</p>
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-800">{totalModules}</p>
                                <p className="text-xs text-gray-500">Modules</p>
                            </div>
                        </div>
                    </div>

                    {/* Instructor Info */}
                    {courseData.instructor && (
                        <div className="border-t pt-4">
                            <div className="flex items-center gap-2 mb-3">
                                <GraduationCap size={18} className="text-gray-600" />
                                <h3 className="font-semibold">Instructor</h3>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                                    {courseData.instructor.profile_image ? (
                                        <img src={courseData.instructor.profile_image} alt={courseData.instructor.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <User size={24} className="text-gray-500" />
                                    )}
                                </div>
                                <div>
                                    <h4 className="font-medium">{courseData.instructor.name}</h4>
                                    <p className="text-sm text-gray-500">{courseData.instructor.credentials}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Protected Content Notice */}
                    <div className="border-l-4 border-red-500 pl-3 py-2 bg-red-50 rounded-r-lg">
                        <div className="flex items-center gap-2">
                            <Shield size={16} className="text-red-600" />
                            <p className="text-sm font-medium text-red-600">Protected Content</p>
                        </div>
                        <p className="text-xs text-red-500 mt-0.5 ml-6">Recording or redistribution prohibited</p>
                    </div>

                    {/* Requirements */}
                    {courseData.requirements && courseData.requirements.length > 0 && (
                        <div className="border-t pt-4">
                            <div className="flex items-center gap-2 mb-2">
                                <CheckCircle2 size={16} className="text-green-500" />
                                <h3 className="font-semibold">Requirements</h3>
                            </div>
                            <ul className="space-y-1 text-sm text-gray-600">
                                {courseData.requirements.map((req, index) => (
                                    <li key={index} className="flex items-start gap-2">
                                        <span className="text-green-500 mt-0.5">•</span>
                                        <span>{req}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* What You'll Learn */}
                    {courseData.what_you_learn && courseData.what_you_learn.length > 0 && (
                        <div className="border-t pt-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Target size={16} className="text-green-500" />
                                <h3 className="font-semibold">What You'll Learn</h3>
                            </div>
                            <ul className="space-y-1 text-sm text-gray-600">
                                {courseData.what_you_learn.slice(0, 5).map((item, index) => (
                                    <li key={index} className="flex items-start gap-2">
                                        <CheckCircle2 size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                                {courseData.what_you_learn.length > 5 && (
                                    <li className="text-green-600 text-xs font-medium">+{courseData.what_you_learn.length - 5} more</li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CoursePlayer;